import { useCanAccess, useGetIdentity, useGetList } from "ra-core";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * A single number, big.
 *
 * Counts are tiles rather than chart marks on purpose: one value is read faster
 * as a number than as a bar, and a row of tiles is the fastest thing to scan.
 */
const StatTile = ({
  label,
  value,
  to,
  isPending,
}: {
  label: string;
  value: number | undefined;
  to: string;
  isPending: boolean;
}) => (
  <Link to={to} className="block">
    <Card className="hover:bg-accent/40 transition-colors">
      <CardContent className="p-4">
        <div className="text-sm text-muted-foreground">{label}</div>
        {isPending ? (
          <Skeleton className="h-8 w-12 mt-1" />
        ) : (
          <div className="text-3xl font-semibold tabular-nums">
            {value ?? 0}
          </div>
        )}
      </CardContent>
    </Card>
  </Link>
);

/**
 * What each role needs at a glance.
 *
 * Every tile is gated on the permission that guards the page it links to, so
 * the row composes itself per role with no branching on role names: an admin
 * sees five tiles, a developer sees the two that mean anything to them.
 *
 * Counts come from RLS-scoped queries, so "open tickets" means the ones that
 * viewer may see and nothing more.
 */
export const RoleStats = () => {
  const { identity } = useGetIdentity();

  const canLeads = useCanAccess({ resource: "deals", action: "list" });
  const canProjects = useCanAccess({ resource: "projects", action: "list" });
  const canTasks = useCanAccess({ resource: "tasks", action: "list" });
  const canTickets = useCanAccess({ resource: "tickets", action: "list" });
  const canUsers = useCanAccess({ resource: "sales", action: "list" });

  // perPage 1 because only the total is wanted; PostgREST returns the count in
  // a header and the row is discarded.
  const one = { pagination: { page: 1, perPage: 1 } };

  const leads = useGetList(
    "deals",
    { ...one, filter: { "archived_at@is": null } },
    { enabled: canLeads.canAccess === true },
  );
  const projects = useGetList(
    "projects",
    { ...one, filter: { status: "active" } },
    { enabled: canProjects.canAccess === true },
  );
  const myTasks = useGetList(
    "tasks",
    { ...one, filter: { assignee_id: identity?.id, "done_date@is": null } },
    { enabled: canTasks.canAccess === true && identity?.id != null },
  );
  const openTickets = useGetList(
    "tickets",
    { ...one, filter: { "status@in": "(open,in_progress)" } },
    { enabled: canTickets.canAccess === true },
  );
  const users = useGetList(
    "sales",
    { ...one, filter: { disabled: false } },
    { enabled: canUsers.canAccess === true },
  );

  const tiles = [
    canLeads.canAccess && {
      label: "Open leads",
      value: leads.total,
      to: "/deals",
      isPending: leads.isPending,
    },
    canProjects.canAccess && {
      label: "Active projects",
      value: projects.total,
      to: "/projects",
      isPending: projects.isPending,
    },
    canTasks.canAccess && {
      label: "My open tasks",
      value: myTasks.total,
      to: "/tasks",
      isPending: myTasks.isPending,
    },
    canTickets.canAccess && {
      label: "Open tickets",
      value: openTickets.total,
      to: "/tickets",
      isPending: openTickets.isPending,
    },
    canUsers.canAccess && {
      label: "People",
      value: users.total,
      to: "/sales",
      isPending: users.isPending,
    },
  ].filter(Boolean) as {
    label: string;
    value: number | undefined;
    to: string;
    isPending: boolean;
  }[];

  if (tiles.length === 0) return null;

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-5">
      {tiles.map((tile) => (
        <StatTile key={tile.label} {...tile} />
      ))}
    </div>
  );
};
